import { useEffect, useState } from "react";
import SideBar from "./SideBar";
import { useAuth } from "../../firebase/authContext";
import { useNavigate } from "react-router-dom";
import DanceStylesPopup from "./DanceStylesPopUp";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../firebase/firebaseConfig";
import { AiOutlineLeft, AiOutlineRight } from "react-icons/ai";
import { IoPlaySharp } from "react-icons/io5"; // Play button icon
import axios from "axios";

function HomePageAuth({ isSidebarOpen, toggleSidebar }) {
  const { isLoggedIn, currentUser } = useAuth();
  const [showPopup, setShowPopup] = useState(false);
  const [videos, setVideos] = useState([]);
  const [scrollPositions, setScrollPositions] = useState({});

  const navigate = useNavigate();

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const response = await axios.get(
          "https://dance-ai-app.azurewebsites.net/api/videos"
        );
        setVideos(response.data.videos);
      } catch (error) {
        console.error("Error fetching videos:", error);
      }
    };
    fetchVideos();
  }, []);

  useEffect(() => {
    if (!isLoggedIn) {
      navigate("/");
    } else {
      const checkDanceStyles = async () => {
        try {
          const userDoc = await getDoc(doc(db, "users", currentUser.uid));
          setShowPopup(!(userDoc.exists() && userDoc.data().danceStyles));
        } catch (error) {
          console.error("Error checking dance styles: ", error);
        }
      };
      checkDanceStyles();
    }
  }, [isLoggedIn, navigate, currentUser]);

  const handleScroll = (id, direction) => {
    const scrollContainer = document.getElementById(id);
    const scrollAmount = 300 * (direction === "left" ? -1 : 1);
    scrollContainer.scrollBy({ left: scrollAmount, behavior: "smooth" });

    setTimeout(() => {
      setScrollPositions((prev) => ({
        ...prev,
        [id]: scrollContainer.scrollLeft > 0,
      }));
    }, 300);
  };

  const videoSections = [
    { id: "trending", title: "Trending Videos", videos: videos.slice(0, 10) },
    { id: "featured-classes", title: "Featured Classes", type: "classes" },
    {
      id: "featured-playlists",
      title: "Featured Playlists",
      videos: videos.slice(10, 20),
    },
    { id: "moves", title: "Moves", videos: videos.slice(20, 30) },
    {
      id: "techniques",
      title: "Techniques & Fundamentals",
      videos: videos.slice(0, 10),
    },
    {
      id: "saved-classes",
      title: "Saved Classes",
      videos: videos.slice(10, 20),
    },
    {
      id: "previous-classes",
      title: "Continue Previous Class",
      videos: videos.slice(20, 30),
    },
    {
      id: "suggestions",
      title: "Suggestions Based on Dance Styles",
      videos: videos.slice(0, 10),
    },
    { id: "workouts", title: "Workouts", videos: videos.slice(10, 20) },
    {
      id: "todays-classes",
      title: "Today's Classes",
      videos: videos.slice(20, 30),
    },
  ];

  return (
    <div className="flex h-full flex-col">
      {showPopup && <DanceStylesPopup />}
      <div className="flex flex-grow">
        {isSidebarOpen && (
          <SideBar
            isSidebarOpen={isSidebarOpen}
            toggleSidebar={toggleSidebar}
          />
        )}
        <div
          className={`p-6 overflow-y-auto transition-all duration-300 ease-in-out ${
            isSidebarOpen ? "w-full lg:w-[calc(100%-250px)]" : "w-full"
          }`}
        >
          {/* Video Sections */}
          {videoSections.map(({ id, title, videos, type }) => (
            <div key={id} className="mb-8">
              <h2 className="text-2xl font-bold mb-4">{title}</h2>

              {type === "classes" ? (
                <div className="flex flex-wrap justify-start gap-4 w-full px-4">
                  {[
                    "Basics",
                    "Choreography",
                    "Freestyle",
                    "Movement Restoration",
                  ].map((category, index) => (
                    <div
                      key={index}
                      className="relative bg-gray-200 rounded-lg shadow-md p-3 w-[350px] h-[220px] flex flex-col justify-between"
                    >
                      <div className="absolute top-1 left-1 bg-black text-white text-xs px-2 py-1 rounded">
                        {category}
                      </div>
                      <div className="w-full h-[90px] bg-gray-300 rounded-lg flex items-center justify-center">
                        🎥 {/* Placeholder for stacked video icon */}
                      </div>
                      <p className="mt-2 text-center font-semibold text-sm">
                        {category} Class
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="relative flex items-center">
                  {scrollPositions[id] && (
                    <div
                      className="absolute left-0 p-2 bg-white shadow-md cursor-pointer rounded-full z-30"
                      onClick={() => handleScroll(id, "left")}
                    >
                      <AiOutlineLeft className="w-5 h-5 text-black" />
                    </div>
                  )}
                  <div
                    id={id}
                    className="flex gap-4 overflow-x-scroll scrollbar-hide px-4"
                  >
                    {videos.length === 0 ? (
                      <p className="text-gray-500 text-center w-full">
                        No videos found.
                      </p>
                    ) : (
                      videos.map((videoUrl, index) => (
                        <div
                          key={index}
                          className="relative min-w-[350px] h-[220px] bg-gray-200 rounded-lg shadow-md overflow-hidden group"
                        >
                          <video
                            src={videoUrl}
                            className="w-full h-full object-cover transition-transform duration-200 ease-in-out group-hover:scale-105"
                            onMouseOver={(e) => e.target.play()}
                            onMouseOut={(e) => {
                              e.target.pause();
                              e.target.currentTime = 0;
                            }}
                            muted
                            loop
                          />
                          {/* Name & Duration Below the Video */}
                          <div className="absolute bottom-0 left-0 w-full bg-white text-black p-2">
                            <p className="font-bold text-sm">
                              Video {index + 1}
                            </p>
                            <p className="text-xs text-gray-600 font-bold">
                              3 min | Hip-Hop
                            </p>
                          </div>

                          {/* Play Button (Only on Hover) */}
                          <button
                            className="absolute bottom-2 right-2 bg-black bg-opacity-50 text-white text-xs p-2 rounded-full opacity-0 group-hover:opacity-100 transition"
                            onClick={() =>
                              navigate("/review", {
                                state: {
                                  videoUrl: videoUrl,
                                  videoFilename: videoUrl.split("/").pop(),
                                },
                              })
                            }
                          >
                            Learn
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                  <div
                    className="absolute right-0 p-2 bg-white shadow-md cursor-pointer rounded-full"
                    onClick={() => handleScroll(id, "right")}
                  >
                    <AiOutlineRight className="w-5 h-5 text-black" />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default HomePageAuth;
